import React, { useState } from 'react';
import CollapsibleSection from './CollapsibleSection';
import InsuranceSection from './InsuranceSection';
import VoiceInputButton from './VoiceInputButton';
import { AccessibilityService } from '../services/AccessibilityService';
import { HopeVoiceService } from '../services/HopeVoiceService';

// Available Pharmacy Options - Reference list organized by service areas
// Phone numbers and exact addresses should be confirmed by patients
const availablePharmacies: Pharmacy[] = [
  {
    id: '1',
    name: 'CVS Pharmacy - Downtown Atlanta',
    address: 'Downtown Atlanta, GA 30309',
    phone: 'Contact pharmacy directly',
    hours: 'Mon-Sat: 8AM-10PM, Sun: 9AM-9PM',
    acceptsInsurance: ['Blue Cross Blue Shield', 'Aetna', 'Cigna', 'UnitedHealthcare'],
    zipCode: '30309',
    serviceZipCodes: ['30309', '30308', '30313', '30314', '30318']
  },
  {
    id: '2',
    name: 'Walgreens - Midtown Atlanta',
    address: 'Midtown Atlanta, GA 30309',
    phone: 'Contact pharmacy directly',
    hours: 'Mon-Fri: 7AM-11PM, Sat-Sun: 8AM-10PM',
    acceptsInsurance: ['Blue Cross Blue Shield', 'Anthem', 'Humana', 'Cigna'],
    zipCode: '30309',
    serviceZipCodes: ['30309', '30308', '30305', '30306', '30312']
  },
  {
    id: '3',
    name: 'Rite Aid - Virginia Highland',
    address: 'Virginia Highland, Atlanta, GA 30306',
    phone: 'Contact pharmacy directly',
    hours: 'Mon-Sun: 8AM-9PM',
    acceptsInsurance: ['UnitedHealthcare', 'Aetna', 'Blue Cross Blue Shield'],
    zipCode: '30306',
    serviceZipCodes: ['30306', '30307', '30309', '30324']
  },
  {
    id: '4',
    name: 'Kroger Pharmacy - Buckhead',
    address: 'Buckhead, Atlanta, GA 30305',
    phone: 'Contact pharmacy directly',
    hours: 'Mon-Sun: 9AM-9PM',
    acceptsInsurance: ['Blue Cross Blue Shield', 'Cigna', 'Anthem', 'Humana'],
    zipCode: '30305',
    serviceZipCodes: ['30305', '30327', '30342', '30319', '30309']
  },
  {
    id: '5',
    name: 'Publix Pharmacy - Decatur',
    address: 'Decatur, GA 30033',
    phone: 'Contact pharmacy directly',
    hours: 'Mon-Sat: 9AM-9PM, Sun: 11AM-6PM',
    acceptsInsurance: ['Blue Cross Blue Shield', 'UnitedHealthcare', 'Aetna', 'Cigna'],
    zipCode: '30033',
    serviceZipCodes: ['30033', '30030', '30032', '30307', '30316']
  },
  {
    id: '6',
    name: 'Independent Community Pharmacy',
    address: 'East Atlanta, GA 30312',
    phone: 'Contact pharmacy directly',
    hours: 'Mon-Fri: 9AM-7PM, Sat: 9AM-5PM',
    acceptsInsurance: ['Blue Cross Blue Shield', 'Aetna', 'Local Plans'],
    zipCode: '30312',
    serviceZipCodes: ['30312', '30315', '30316', '30309']
  },
  {
    id: '7',
    name: 'CVS Pharmacy - Marietta',
    address: 'Marietta, GA 30062',
    phone: 'Contact pharmacy directly',
    hours: 'Mon-Sat: 8AM-10PM, Sun: 9AM-9PM',
    acceptsInsurance: ['Blue Cross Blue Shield', 'Aetna', 'Cigna', 'UnitedHealthcare'],
    zipCode: '30062',
    serviceZipCodes: ['30062', '30064', '30066', '30067', '30060']
  },
  {
    id: '8',
    name: 'Walgreens - Alpharetta',
    address: 'Alpharetta, GA 30022',
    phone: 'Contact pharmacy directly',
    hours: 'Mon-Fri: 7AM-11PM, Sat-Sun: 8AM-10PM',
    acceptsInsurance: ['Blue Cross Blue Shield', 'Anthem', 'Humana', 'Cigna'],
    zipCode: '30022',
    serviceZipCodes: ['30022', '30004', '30005', '30009', '30024']
  }
];

interface Doctor {
  name: string;
  specialty: string;
  phone: string;
  email: string;
  practiceName: string;
  address: string;
}

interface Caregiver {
  name: string;
  relationship: string;
  phone: string;
  email: string;
  hasLegalAuthority: boolean;
  authorityDocuments?: File[];
}

interface EmergencyContact {
  name: string;
  relationship: string;
  phone: string;
  isAuthorizedForMedicalDecisions: boolean;
}

interface Insurance {
  provider: string;
  planType: string;
  memberId: string;
  groupNumber: string;
  copay: string;
  deductible: string;
  outOfPocketMax: string;
}

interface Pharmacy {
  id: string;
  name: string;
  address: string;
  phone: string;
  hours: string;
  acceptsInsurance: string[];
  zipCode: string;
  serviceZipCodes: string[]; // Zip codes this pharmacy serves
  distanceFromClient?: number; // Calculated distance in miles
}

interface ClientIntakeData {
  reasonForVisit: string;
  symptoms: string;
  zipCode?: string;
  primaryConcerns: string[];
  currentDoctors: Doctor[];
  medications: {
    name: string;
    dosage: string;
    frequency: string;
    prescribedBy: string;
  }[];
  allergies: {
    type: 'medication' | 'food' | 'environmental';
    name: string;
    reaction: string;
    severity: 'mild' | 'moderate' | 'severe';
  }[];
  chronicConditions: string[];
  emergencyContacts: EmergencyContact[];
  caregiver?: Caregiver;
  insurance: Insurance;
  preferredPharmacy?: Pharmacy;
  formsAndConsents: {
    medicalRelease: boolean;
    hipaaConsent: boolean;
    caregiverAuthorization: boolean;
  };
}

interface ClientInfo {
  firstName?: string;
  lastName?: string;
  dateOfBirth?: string;
  phone?: string;
  email?: string;
  address?: string;
  city?: string;
  state?: string;
  zipCode?: string;
}

interface Props {
  isInitialSetup?: boolean;
  existingData?: ClientIntakeData;
  clientInfo?: ClientInfo;
  onSubmit: (data: ClientIntakeData) => void;
}

const ClientIntakeForm: React.FC<Props> = ({ isInitialSetup = false, existingData, clientInfo, onSubmit }) => {
  const [formData, setFormData] = useState<ClientIntakeData>(existingData || {
    reasonForVisit: '',
    symptoms: '',
    zipCode: '',
    primaryConcerns: [],
    currentDoctors: [],
    medications: [],
    allergies: [],
    chronicConditions: [],
    emergencyContacts: [],
    insurance: {
      provider: '',
      planType: '',
      memberId: '',
      groupNumber: '',
      copay: '',
      deductible: '',
      outOfPocketMax: ''
    },
    formsAndConsents: {
      medicalRelease: false,
      hipaaConsent: false,
      caregiverAuthorization: false,
    }
  });

  const [activeSection] = useState<string>(isInitialSetup ? 'reasonForVisit' : '');

  // Filter pharmacies based on client's zip code
  const getAvailablePharmacies = (): Pharmacy[] => {
    if (!formData.zipCode) return availablePharmacies;

    return availablePharmacies.filter(pharmacy =>
      pharmacy.serviceZipCodes.includes(formData.zipCode!)
    ).map(pharmacy => ({
      ...pharmacy,
      distanceFromClient: pharmacy.zipCode === formData.zipCode ? 0.5 : 2.5 // Mock distance calculation
    })).sort((a, b) => (a.distanceFromClient || 999) - (b.distanceFromClient || 999));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  const handleInputChange = (section: keyof ClientIntakeData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [section]: value
    }));
  };

  return (
    <form onSubmit={handleSubmit} className="intake-form">
      <div style={{ maxWidth: '800px', margin: '0 auto' }}>
        {/* Client Info Section (read-only) */}
        {clientInfo && !isInitialSetup && (
          <CollapsibleSection title="Client Information" defaultExpanded={true}>
            <div style={{
              background: '#1c2536',
              borderRadius: '0.75rem',
              padding: '1.5rem',
              color: 'white'
            }}>
              <div style={{ display: 'grid', gap: '1rem', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.25rem', color: '#9ca3af', fontSize: '0.875rem' }}>Full Name</label>
                  <p style={{ margin: 0, color: 'white', fontWeight: '500' }}>
                    {`${clientInfo.firstName || ''} ${clientInfo.lastName || ''}`.trim() || 'N/A'}
                  </p>
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.25rem', color: '#9ca3af', fontSize: '0.875rem' }}>Date of Birth</label>
                  <p style={{ margin: 0, color: 'white', fontWeight: '500' }}>
                    {clientInfo.dateOfBirth
                      ? clientInfo.dateOfBirth.split('-').length === 3
                        ? `${clientInfo.dateOfBirth.split('-')[1]}/${clientInfo.dateOfBirth.split('-')[2]}/${clientInfo.dateOfBirth.split('-')[0]}`
                        : clientInfo.dateOfBirth
                      : 'N/A'}
                  </p>
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.25rem', color: '#9ca3af', fontSize: '0.875rem' }}>Phone</label>
                  <p style={{ margin: 0, color: 'white', fontWeight: '500' }}>
                    {clientInfo.phone || 'N/A'}
                  </p>
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.25rem', color: '#9ca3af', fontSize: '0.875rem' }}>Email</label>
                  <p style={{ margin: 0, color: 'white', fontWeight: '500' }}>
                    {clientInfo.email || 'N/A'}
                  </p>
                </div>
              </div>
              {(clientInfo.address || clientInfo.city || clientInfo.state) && (
                <div style={{ marginTop: '1rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.25rem', color: '#9ca3af', fontSize: '0.875rem' }}>Address</label>
                  <p style={{ margin: 0, color: 'white', fontWeight: '500' }}>
                    {[clientInfo.address, clientInfo.city, clientInfo.state, clientInfo.zipCode].filter(Boolean).join(', ')}
                  </p>
                </div>
              )}
            </div>
          </CollapsibleSection>
        )}

        {/* Primary Concern Section */}
        <CollapsibleSection
          title="Reason for Visit"
          defaultExpanded={isInitialSetup || activeSection === 'reasonForVisit'}
        >
          <div style={{
            background: '#1c2536',
            borderRadius: '0.75rem',
            padding: '1.5rem',
            color: 'white'
          }}>
            {/* Zip Code Field - First for pharmacy filtering */}
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', color: '#d1d5db' }}>
                Your Zip Code <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <input
                  type="text"
                  value={formData.zipCode || ''}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, ''); // Only allow digits
                    if (value.length <= 5) {
                      handleInputChange('zipCode', value);
                    }
                  }}
                  style={{
                    width: '150px',
                    padding: '0.75rem',
                    borderRadius: '0.375rem',
                    border: '1px solid rgba(156, 163, 175, 0.2)',
                    background: 'rgba(255, 255, 255, 0.1)',
                    color: 'white',
                    fontSize: '1rem'
                  }}
                  placeholder="30309"
                  required={isInitialSetup}
                  maxLength={5}
                />
                <VoiceInputButton onResult={(text) => { const digits = text.replace(/\D/g, '').slice(0, 5); if (digits) handleInputChange('zipCode', digits); }} size={18} />
              </div>
              <p style={{ color: '#d1d5db', fontSize: '0.875rem', margin: '0.5rem 0 0 0' }}>
                This helps us find pharmacies near you
              </p>
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', color: '#d1d5db' }}>
                Primary Reason for Visit
              </label>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                <textarea
                  value={formData.reasonForVisit}
                  onChange={(e) => handleInputChange('reasonForVisit', e.target.value)}
                  style={{
                    flex: 1,
                    padding: '0.75rem',
                    borderRadius: '0.375rem',
                    border: '1px solid rgba(156, 163, 175, 0.2)',
                    background: 'rgba(255, 255, 255, 0.1)',
                    color: 'white',
                    minHeight: '100px'
                  }}
                  placeholder="Please describe your main reason for seeking care..."
                  required={isInitialSetup}
                />
                <VoiceInputButton onResult={(text) => handleInputChange('reasonForVisit', (formData.reasonForVisit ? formData.reasonForVisit + ' ' : '') + text)} size={20} />
              </div>
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', color: '#d1d5db' }}>
                Current Symptoms
              </label>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                <textarea
                  value={formData.symptoms}
                  onChange={(e) => handleInputChange('symptoms', e.target.value)}
                  style={{
                    flex: 1,
                    padding: '0.75rem',
                    borderRadius: '0.375rem',
                    border: '1px solid rgba(156, 163, 175, 0.2)',
                    background: 'rgba(255, 255, 255, 0.1)',
                    color: 'white',
                    minHeight: '100px'
                  }}
                  placeholder="Please describe any current symptoms you're experiencing..."
                  required={isInitialSetup}
                />
                <VoiceInputButton onResult={(text) => handleInputChange('symptoms', (formData.symptoms ? formData.symptoms + ' ' : '') + text)} size={20} />
              </div>
            </div>
          </div>
        </CollapsibleSection>

        {/* Current Doctors Section */}
        <CollapsibleSection 
          title="Current Healthcare Providers" 
          defaultExpanded={isInitialSetup || activeSection === 'doctors'}
        >
          <div style={{
            background: '#1c2536',
            borderRadius: '0.75rem',
            padding: '1.5rem',
            color: 'white'
          }}>
            {formData.currentDoctors.map((doctor, index) => (
              <div key={index} style={{ 
                marginBottom: '1rem',
                padding: '1rem',
                background: 'rgba(255, 255, 255, 0.1)',
                borderRadius: '0.5rem'
              }}>
                <div style={{ display: 'grid', gap: '1rem', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', color: '#d1d5db' }}>
                      Doctor's Name
                    </label>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <input
                        type="text"
                        value={doctor.name}
                        onChange={(e) => {
                          const updatedDoctors = [...formData.currentDoctors];
                          updatedDoctors[index].name = e.target.value;
                          handleInputChange('currentDoctors', updatedDoctors);
                        }}
                        style={{
                          flex: 1,
                          padding: '0.5rem',
                          borderRadius: '0.375rem',
                          border: '1px solid rgba(156, 163, 175, 0.2)',
                          background: 'rgba(255, 255, 255, 0.1)',
                          color: 'white'
                        }}
                        required={isInitialSetup}
                      />
                      <VoiceInputButton onResult={(text) => { const updatedDoctors = [...formData.currentDoctors]; updatedDoctors[index].name = doctor.name ? doctor.name + ' ' + text : text; handleInputChange('currentDoctors', updatedDoctors); }} size={18} />
                    </div>
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', color: '#d1d5db' }}>
                      Specialty
                    </label>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <input
                        type="text"
                        value={doctor.specialty}
                        onChange={(e) => {
                          const updatedDoctors = [...formData.currentDoctors];
                          updatedDoctors[index].specialty = e.target.value;
                          handleInputChange('currentDoctors', updatedDoctors);
                        }}
                        style={{
                          flex: 1,
                          padding: '0.5rem',
                          borderRadius: '0.375rem',
                          border: '1px solid rgba(156, 163, 175, 0.2)',
                          background: 'rgba(255, 255, 255, 0.1)',
                          color: 'white'
                        }}
                        required={isInitialSetup}
                      />
                      <VoiceInputButton onResult={(text) => { const updatedDoctors = [...formData.currentDoctors]; updatedDoctors[index].specialty = doctor.specialty ? doctor.specialty + ' ' + text : text; handleInputChange('currentDoctors', updatedDoctors); }} size={18} />
                    </div>
                  </div>
                </div>
                {/* Add more doctor fields here */}
              </div>
            ))}
            <button
              type="button"
              onClick={() => {
                handleInputChange('currentDoctors', [...formData.currentDoctors, {
                  name: '',
                  specialty: '',
                  phone: '',
                  email: '',
                  practiceName: '',
                  address: ''
                }]);
              }}
              style={{
                background: '#D4A017',
                color: 'white',
                border: 'none',
                borderRadius: '0.375rem',
                padding: '0.5rem 1rem',
                cursor: 'pointer',
                marginTop: '1rem'
              }}
            >
              Add Healthcare Provider
            </button>
          </div>
        </CollapsibleSection>

        {/* Emergency Contact & Caregiver Section */}
        <CollapsibleSection 
          title="Emergency Contacts & Caregiver Information" 
          defaultExpanded={isInitialSetup || activeSection === 'contacts'}
        >
          <div style={{
            background: '#1c2536',
            borderRadius: '0.75rem',
            padding: '1.5rem',
            color: 'white'
          }}>
            {/* Emergency Contacts */}
            <h4 style={{ color: '#d1d5db', marginTop: 0 }}>Emergency Contacts</h4>
            {formData.emergencyContacts.map((contact, index) => (
              <div key={index} style={{ 
                marginBottom: '1rem',
                padding: '1rem',
                background: 'rgba(255, 255, 255, 0.1)',
                borderRadius: '0.5rem'
              }}>
                {/* Emergency contact fields */}
              </div>
            ))}
            <button
              type="button"
              onClick={() => {
                handleInputChange('emergencyContacts', [...formData.emergencyContacts, {
                  name: '',
                  relationship: '',
                  phone: '',
                  isAuthorizedForMedicalDecisions: false
                }]);
              }}
              style={{
                background: '#D4A017',
                color: 'white',
                border: 'none',
                borderRadius: '0.375rem',
                padding: '0.5rem 1rem',
                cursor: 'pointer',
                marginTop: '1rem'
              }}
            >
              Add Emergency Contact
            </button>

            {/* Caregiver Information */}
            <h4 style={{ color: '#d1d5db', marginTop: '2rem' }}>Caregiver Information</h4>
            {/* Add caregiver fields here */}
          </div>
        </CollapsibleSection>

        {/* Insurance Information Section */}
        <CollapsibleSection
          title="Insurance Information"
          defaultExpanded={isInitialSetup || activeSection === 'insurance'}
        >
          <InsuranceSection
            insurance={formData.insurance || {
              provider: '',
              planType: '',
              memberId: '',
              groupNumber: '',
              copay: '',
              deductible: '',
              outOfPocketMax: ''
            }}
            onChange={(insurance) => handleInputChange('insurance', insurance)}
            isInitialSetup={isInitialSetup}
          />
        </CollapsibleSection>

        {/* Preferred Pharmacy Section */}
        <CollapsibleSection
          title="Preferred Pharmacy"
          defaultExpanded={isInitialSetup || activeSection === 'pharmacy'}
        >
          <div style={{
            background: '#1c2536',
            borderRadius: '0.75rem',
            padding: '1.5rem',
            color: 'white'
          }}>
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', color: '#d1d5db' }}>
                Select Your Preferred Pharmacy
              </label>
              <p style={{ color: '#d1d5db', fontSize: '0.875rem', marginBottom: '1rem' }}>
                {formData.zipCode
                  ? `Showing pharmacies serving zip code ${formData.zipCode} (sorted by distance):`
                  : 'Please enter your zip code above to see nearby pharmacies.'
                }
              </p>

              {!formData.zipCode && (
                <div style={{
                  padding: '1rem',
                  background: '#FEF3C7',
                  borderRadius: '0.5rem',
                  border: '1px solid #F59E0B',
                  marginBottom: '1rem'
                }}>
                  <p style={{ margin: 0, color: '#92400E', fontSize: '0.875rem' }}>
                    📍 Please enter your zip code above to see pharmacies in your area.
                  </p>
                </div>
              )}

              <div style={{ display: 'grid', gap: '0.75rem' }}>
                {getAvailablePharmacies().map((pharmacy) => (
                  <label key={pharmacy.id} style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '0.75rem',
                    padding: '1rem',
                    background: formData.preferredPharmacy?.id === pharmacy.id
                      ? 'rgba(212, 160, 23, 0.2)'
                      : 'rgba(255, 255, 255, 0.05)',
                    borderRadius: '0.5rem',
                    cursor: 'pointer',
                    border: formData.preferredPharmacy?.id === pharmacy.id
                      ? '2px solid #D4A017'
                      : '1px solid rgba(156, 163, 175, 0.2)',
                    transition: 'all 0.2s'
                  }}>
                    <input
                      type="radio"
                      name="preferredPharmacy"
                      value={pharmacy.id}
                      checked={formData.preferredPharmacy?.id === pharmacy.id}
                      onChange={() => handleInputChange('preferredPharmacy', pharmacy)}
                      style={{ marginTop: '0.25rem' }}
                    />
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                        <h4 style={{ margin: 0, color: 'white', fontSize: '1rem' }}>
                          {pharmacy.name}
                        </h4>
                        {pharmacy.distanceFromClient !== undefined && (
                          <span style={{
                            background: '#10B981',
                            color: 'white',
                            padding: '0.125rem 0.5rem',
                            borderRadius: '0.25rem',
                            fontSize: '0.75rem',
                            fontWeight: '600'
                          }}>
                            {pharmacy.distanceFromClient} mi
                          </span>
                        )}
                      </div>
                      <p style={{ margin: '0 0 0.25rem 0', color: '#d1d5db', fontSize: '0.875rem' }}>
                        📍 {pharmacy.address}
                      </p>
                      <p style={{ margin: '0 0 0.25rem 0', color: '#d1d5db', fontSize: '0.875rem' }}>
                        📞 {pharmacy.phone}
                      </p>
                      <p style={{ margin: '0 0 0.5rem 0', color: '#d1d5db', fontSize: '0.875rem' }}>
                        🕒 {pharmacy.hours}
                      </p>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem' }}>
                        {pharmacy.acceptsInsurance.map((insurance, idx) => (
                          <span key={idx} style={{
                            background: formData.insurance?.provider &&
                              pharmacy.acceptsInsurance.includes(formData.insurance.provider)
                              ? '#16A34A'
                              : '#6B7280',
                            color: 'white',
                            padding: '0.125rem 0.5rem',
                            borderRadius: '0.25rem',
                            fontSize: '0.75rem'
                          }}>
                            {insurance}
                          </span>
                        ))}
                      </div>
                      {formData.insurance?.provider &&
                       !pharmacy.acceptsInsurance.includes(formData.insurance.provider) && (
                        <p style={{
                          color: '#F59E0B',
                          fontSize: '0.75rem',
                          marginTop: '0.5rem',
                          fontStyle: 'italic'
                        }}>
                          ⚠️ This pharmacy may not accept your insurance ({formData.insurance.provider})
                        </p>
                      )}
                    </div>
                  </label>
                ))}
              </div>

              {!formData.preferredPharmacy && (
                <div style={{
                  marginTop: '1rem',
                  padding: '1rem',
                  background: '#FEF3C7',
                  borderRadius: '0.5rem',
                  border: '1px solid #F59E0B'
                }}>
                  <p style={{ margin: 0, color: '#92400E', fontSize: '0.875rem' }}>
                    💡 Please select a preferred pharmacy so your provider knows where to send your prescriptions.
                  </p>
                </div>
              )}
            </div>
          </div>
        </CollapsibleSection>

        {/* Accessibility Preferences */}
        <CollapsibleSection title="Accessibility Preferences" defaultExpanded={false}>
          <div style={{
            background: '#1c2536',
            borderRadius: '0.75rem',
            padding: '1.5rem',
            color: 'white'
          }}>
            <p style={{ color: '#9ca3af', fontSize: '0.9rem', marginBottom: '1rem' }}>
              Need help reading or navigating the app? Select any that apply.
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem' }}>
              <label style={{
                display: 'flex',
                alignItems: 'center',
                color: '#f9fafb',
                cursor: 'pointer',
                padding: '0.75rem',
                background: '#374151',
                borderRadius: '8px'
              }}>
                <input
                  type="checkbox"
                  checked={HopeVoiceService.getSettings().enabled}
                  onChange={(e) => {
                    if (e.target.checked) {
                      HopeVoiceService.enable();
                    } else {
                      HopeVoiceService.disable();
                    }
                  }}
                  style={{ marginRight: '0.75rem' }}
                />
                Read Aloud — Hope reads the screen for me
              </label>

              <label style={{
                display: 'flex',
                alignItems: 'center',
                color: '#f9fafb',
                cursor: 'pointer',
                padding: '0.75rem',
                background: '#374151',
                borderRadius: '8px'
              }}>
                <input
                  type="checkbox"
                  checked={AccessibilityService.getSettings().fontSize === 'large' || AccessibilityService.getSettings().fontSize === 'x-large'}
                  onChange={(e) => {
                    if (e.target.checked) {
                      AccessibilityService.increaseFontSize();
                      AccessibilityService.increaseFontSize();
                    } else {
                      AccessibilityService.decreaseFontSize();
                      AccessibilityService.decreaseFontSize();
                    }
                  }}
                  style={{ marginRight: '0.75rem' }}
                />
                Large Text — make everything bigger
              </label>

              <label style={{
                display: 'flex',
                alignItems: 'center',
                color: '#f9fafb',
                cursor: 'pointer',
                padding: '0.75rem',
                background: '#374151',
                borderRadius: '8px'
              }}>
                <input
                  type="checkbox"
                  checked={AccessibilityService.isHighContrast()}
                  onChange={() => AccessibilityService.toggleHighContrast()}
                  style={{ marginRight: '0.75rem' }}
                />
                High Contrast — bold colors for easier reading
              </label>
            </div>
          </div>
        </CollapsibleSection>

        {/* Submit Button */}
        <div style={{ marginTop: '2rem', textAlign: 'center' }}>
          <button
            type="submit"
            style={{
              background: '#D4A017',
              color: 'white',
              border: 'none',
              borderRadius: '0.5rem',
              padding: '0.75rem 2rem',
              fontSize: '1.1rem',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
          >
            {isInitialSetup ? 'Complete Intake Form' : 'Update Information'}
          </button>
        </div>
      </div>
    </form>
  );
};

export default ClientIntakeForm;
